import { useState, useCallback, useEffect, useRef } from "react";
import {
  ChevronLeft, ChevronRight, ZoomIn, ZoomOut, RotateCw,
  Maximize2, Minimize2, Download, Loader2, FileWarning, ScanText,
  RefreshCw,
} from "lucide-react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { absoluteUrl } from "../lib/media";

if (typeof window !== "undefined" && !pdfjs.GlobalWorkerOptions.workerSrc) {
  pdfjs.GlobalWorkerOptions.workerSrc = `/pdf.worker.min.mjs?v=${pdfjs.version}`;
}

/**
 * DocumentViewer — aperçu PDF / image inline, sûr en mobile.
 *
 * Feb 2026 : le zoom faisait planter les smartphones car chaque changement
 * de `scale` re-rendait la page PDF à une résolution supérieure (memory OOM
 * après ~5 zooms). Correctif : rendu PDF à taille fixe (largeur conteneur),
 * zoom appliqué via `transform: scale()` sur un wrapper — aucune ré-rasterisation.
 */
export default function DocumentViewer({ src, mime, name, dense = false, onFullscreen }) {
  const url = absoluteUrl(src);
  const isPdf =
    (mime || "").toLowerCase().startsWith("application/pdf") ||
    (url || "").toLowerCase().endsWith(".pdf") ||
    (name || "").toLowerCase().endsWith(".pdf");

  const [numPages, setNumPages] = useState(null);
  const [page, setPage] = useState(1);
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const [loadError, setLoadError] = useState(false);
  // Base width = largeur du conteneur au moment du rendu (mesure une fois via
  // ResizeObserver). Le PDF est rasterisé à cette taille et n'est PLUS re-rendu
  // au zoom — le zoom est une simple transformation CSS.
  const viewportRef = useRef(null);
  const [baseWidth, setBaseWidth] = useState(null);

  useEffect(() => { setPage(1); setScale(1); setRotation(0); setLoadError(false); }, [url]);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) {
        // Largeur intérieure — padding déjà retiré
        const w = Math.max(280, Math.min(1080, Math.floor(e.contentRect.width) - 16));
        setBaseWidth(w);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const onDocLoad = useCallback(({ numPages }) => setNumPages(numPages), []);
  const onDocError = useCallback((e) => { console.error("PDF load error", e); setLoadError(true); }, []);

  // Zoom borné à [0,5 ; 2,5] pour éviter les OOM mobile.
  const clampScale = (v) => Math.max(0.5, Math.min(2.5, Math.round(v * 100) / 100));
  const zoomOut = () => setScale((s) => clampScale(s - 0.2));
  const zoomIn  = () => setScale((s) => clampScale(s + 0.2));
  const resetZoom = () => { setScale(1); setRotation(0); };
  const rotate = () => setRotation((r) => (r + 90) % 360);
  const toggleFs = () => { setFullscreen((v) => !v); onFullscreen?.(!fullscreen); };
  const prev = () => setPage((p) => Math.max(1, p - 1));
  const next = () => setPage((p) => Math.min(numPages || p, p + 1));

  if (!url) {
    return (
      <div className="h-[400px] flex items-center justify-center text-[13px] text-[#5E6875]">
        Aucun document original.
      </div>
    );
  }

  const btn = "w-8 h-8 rounded-md flex items-center justify-center text-white/90 hover:bg-white/10 disabled:opacity-40 shrink-0";
  const wrapCls = fullscreen
    ? "fixed inset-0 z-[80] bg-[#0B1F33]"
    : "relative bg-[#F4F5EF]";

  return (
    <div className={wrapCls} data-testid="document-viewer">
      {/* Toolbar — wrap en mobile pour éviter tout débordement horizontal */}
      <div className="sticky top-0 z-10 bg-[#0B1F33] text-white px-3 py-2 flex items-center gap-1.5 flex-wrap">
        <ScanText className="w-4 h-4 text-[#FFD166] shrink-0" />
        <span className="text-[12.5px] font-bold truncate max-w-[40%]" title={name}>{name || "Document"}</span>

        {isPdf && numPages > 1 && (
          <div className="flex items-center gap-1 ml-2">
            <button data-testid="doc-prev" onClick={prev} disabled={page <= 1} className={btn} title="Page précédente"><ChevronLeft className="w-4 h-4" /></button>
            <span className="text-[12px] font-mono tabular-nums min-w-[54px] text-center">{page} / {numPages}</span>
            <button data-testid="doc-next" onClick={next} disabled={page >= numPages} className={btn} title="Page suivante"><ChevronRight className="w-4 h-4" /></button>
          </div>
        )}

        <div className="flex items-center gap-1 ml-auto flex-wrap justify-end">
          <button data-testid="doc-zoom-out" onClick={zoomOut} disabled={scale <= 0.5} className={btn} title="Zoom arrière"><ZoomOut className="w-4 h-4" /></button>
          <span className="text-[11.5px] font-mono tabular-nums w-10 text-center">{Math.round(scale * 100)}%</span>
          <button data-testid="doc-zoom-in"  onClick={zoomIn}  disabled={scale >= 2.5} className={btn} title="Zoom avant"><ZoomIn className="w-4 h-4" /></button>
          <button data-testid="doc-zoom-reset" onClick={resetZoom} className={btn} title="Réinitialiser le zoom"><RefreshCw className="w-4 h-4" /></button>
          <button data-testid="doc-rotate" onClick={rotate} className={btn} title="Rotation"><RotateCw className="w-4 h-4" /></button>
          <button data-testid="doc-fullscreen" onClick={toggleFs} className={btn} title={fullscreen ? "Quitter plein écran" : "Plein écran"}>
            {fullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
          <a
            data-testid="doc-download"
            href={url}
            download={name || true}
            className={`${btn} text-[#FFD166]`}
            title="Télécharger (action secondaire)"
            onClick={(e) => e.stopPropagation()}
          >
            <Download className="w-4 h-4" />
          </a>
        </div>
      </div>

      {/* Viewport : le zoom est appliqué en CSS sur un wrapper — le PDF est
          rendu à `baseWidth` (une fois, via ResizeObserver) puis simplement
          transformé, ce qui évite toute ré-rasterisation mobile. */}
      <div
        ref={viewportRef}
        className={`overflow-auto ${fullscreen ? "h-[calc(100vh-44px)]" : "max-h-[720px]"} flex items-start justify-center p-4 touch-pan-x touch-pan-y`}
        style={{ overscrollBehavior: "contain" }}
      >
        {isPdf ? (
          loadError ? (
            <div className="text-center py-12 text-[#5E6875]">
              <FileWarning className="w-8 h-8 mx-auto text-[#D84343] mb-2" />
              <p className="text-[13.5px] font-semibold text-[#0B1F33]">Impossible d'afficher le document</p>
              <p className="text-[12px]">Le fichier est peut-être temporairement indisponible. Réessayez ou téléchargez-le.</p>
            </div>
          ) : (
            <div
              style={{
                transform: `scale(${scale})`,
                transformOrigin: "top center",
                willChange: "transform",
                transition: "transform 120ms ease-out",
              }}
            >
              <Document
                file={url}
                onLoadSuccess={onDocLoad}
                onLoadError={onDocError}
                loading={
                  <div className="flex flex-col items-center gap-2 py-10 text-[#5E6875] text-[13px]">
                    <Loader2 className="w-6 h-6 animate-spin" /> Chargement du document…
                  </div>
                }
                error={null}
                className="flex flex-col items-center gap-3"
              >
                <Page
                  pageNumber={page}
                  rotate={rotation}
                  width={baseWidth || 320}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                  className="shadow-[0_20px_60px_rgba(11,31,51,0.25)] bg-white"
                />
              </Document>
            </div>
          )
        ) : (
          <img
            src={url}
            alt={name || "Document"}
            onError={() => setLoadError(true)}
            style={{
              transform: `scale(${scale}) rotate(${rotation}deg)`,
              transformOrigin: "top center",
              willChange: "transform",
              transition: "transform 120ms ease-out",
            }}
            className="max-w-full bg-white shadow-[0_20px_60px_rgba(11,31,51,0.25)]"
          />
        )}
      </div>
    </div>
  );
}
